import { useMemo } from "react";

import { MenuItem, Paper, SelectInput } from "@portex-pro/ui-components";
import { mapModeToTransportationMode } from "app/pages/reports-and-analytics/components/reports/ReportsTabContainer";
import { Mode, ReportType } from "app/pages/reports-and-analytics/types/client";
import LastRefreshedAtText from "components/LastRefreshedAtText";
import RefetchButton from "components/RefetchButton";
import ThrottledTextInput from "components/ThrottledTextInput";
import withAsync from "components/withAsync";
import ModeButtonGroup from "pages/shipper/components/ModeButtonGroup";
import { TransportationMode } from "pages/shipper/pages/quotes/types/TransportationMode";
import { useTranslation } from "react-i18next";

import { useGetBrokerReportTypeListQuery } from "../api/brokerInsightsApi";
import { BrokerInsightsIndexFilterState } from "../store/brokerInsightsIndexFilterSlice";
import BrokerInsightsDateFilterView from "./BrokerInsightsDateFilterView";
import BrokerInsightsExportView from "./BrokerInsightsExportView";

interface BrokerInsightsFilterBarViewProps {
  filters?: JSX.Element;
  mode: TransportationMode;
  searchQuery: string;
  reportId?: number;
  lastRefreshedAt?: string;
  isLoading: boolean;
  reportTypes: ReportType[];
  selectedReport: ReportType;

  onChangeFilter: (changes: Partial<BrokerInsightsIndexFilterState>) => void;
  onRefresh: () => void;
}

const useLoadData = () => {
  const result = useGetBrokerReportTypeListQuery({});

  return result;
};

const BrokerInsightsFilterBarView = withAsync<BrokerInsightsFilterBarViewProps, ReturnType<typeof useLoadData>>({
  useHook: useLoadData,
  Component: ({
    filters,
    mode,
    searchQuery,
    reportId,
    lastRefreshedAt,
    isLoading,
    reportTypes,
    selectedReport,
    onChangeFilter,
    onRefresh,
    loadedData,
  }) => {
    const { t } = useTranslation("insightsV2");
    const searchableFields = selectedReport.searchableFields;
    const {
      data: { reports },
    } = loadedData;

    const renderSearchPlaceholder = () => {
      const fieldCountContext =
        searchableFields?.length === 1 ? "single" : searchableFields?.length === 2 ? "double" : undefined;
      return searchableFields
        ?.map((field, index) =>
          t("searchableFields", { count: searchableFields.length - index, context: fieldCountContext, field })
        )
        .join(" ");
    };

    const supportedModesForReport = useMemo(
      () => reports.find((report) => report.id === reportId),
      [reports, reportId]
    )?.supportsModes;

    const disabledModes = [Mode.Ftl, Mode.Ltl, Mode.LtlPartial, Mode.Drayage, Mode.Fcl, Mode.Air, Mode.Intermodal]
      .filter((mode) => !supportedModesForReport?.includes(mode))
      .map((mode) => mapModeToTransportationMode[mode]);

    return (
      <div className="flex flex-col w-full">
        <Paper style={{ padding: "10px" }}>
          <div className="flex justify-between items-end mb-4">
            {/* left side: one row with all controls, bottom‑aligned */}
            <div className="flex items-end gap-x-2">
              <SelectInput
                label="Report"
                value={reportId ?? ""}
                onChange={(e) =>
                  onChangeFilter({
                    selectedReport: reportTypes.find((reportType) => reportType.id === Number(e.target.value)),
                  })
                }
                SelectProps={{ style: { minWidth: 200 } }}
              >
                {reportTypes.map((reportType) => (
                  <MenuItem value={reportType.id}>{reportType.name}</MenuItem>
                ))}
              </SelectInput>
            </div>

            <div className="flex items-end gap-x-2 whitespace-nowrap">
              <BrokerInsightsExportView />
            </div>
          </div>

          <div className="flex justify-between items-end">
            <div className="flex items-end gap-x-2">
              <ModeButtonGroup
                value={mode}
                onChange={(mode) => onChangeFilter({ mode })}
                includeModes={[TransportationMode.INTERMODAL]}
                excludeModes={[TransportationMode.All]}
                disabledModes={disabledModes}
              />

              {searchableFields && searchableFields.length > 0 && (
                <div style={{ width: "300px" }}>
                  <ThrottledTextInput
                    search
                    placeholder={t("search_by_placeholder", { searchOptions: renderSearchPlaceholder() })}
                    margin="none"
                    fullWidth={true}
                    value={searchQuery || ""}
                    onChange={(searchQuery) => onChangeFilter({ searchQuery })}
                    onClear={() => onChangeFilter({ searchQuery: "" })}
                  />
                </div>
              )}
              <BrokerInsightsDateFilterView />

              {filters}
            </div>

            <div className="flex items-center gap-x-2 whitespace-nowrap">
              {lastRefreshedAt && <LastRefreshedAtText value={lastRefreshedAt} />}
              <RefetchButton loading={isLoading} onClick={onRefresh} />
            </div>
          </div>
        </Paper>
      </div>
    );
  },
});

export default BrokerInsightsFilterBarView;
